import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AiBotService {
    private apiService = inject(ApiService);

    /**
     * Fetches all headline pages for a specific product.
     */
    getHeadlinePages(productId?: number): Observable<any> {
        const productServerName = environment.ai.productServerName;
        const params: any = {};
        if (productId) params.product_id = productId;

        return this.apiService.productGet('headline-pages', productServerName, params, {
            showLoader: false,
            showErrorToaster: true
        });
    }

    /**
     * Fetches a single headline page by ID.
     * GET /bot/headline-pages/{page_id}
     */
    getHeadlinePage(pageId: number): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productGet(`headline-pages/${pageId}`, productServerName, {}, {
            showLoader: false,
            showErrorToaster: true
        });
    }

    /**
     * Adds a new headline page.
     * POST /bot/headline-page/add
     */
    addHeadlinePage(payload: {
        product_id: number;
        name: string;
        keyname: string;
        description: string;
        is_active: boolean;
    }): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productPost('headline-page/add', productServerName, payload, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    /**
     * Updates an existing headline page.
     * PUT /bot/headline-page/update/{page_id}
     */
    updateHeadlinePage(pageId: number, payload: {
        name: string;
        description: string;
        is_active: boolean;
    }): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productPut(`headline-page/update/${pageId}`, productServerName, payload, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    /**
     * Adds a new API to a headline page.
     * POST /bot/headline-page-api/add
     */
    addHeadlinePageApi(payload: {
        headline_page_id: number;
        data_source_id: number;
        api_name: string;
        api_keyname: string;
        base_url: string;
        method: string;
        is_active: boolean;
        config: any;
    }): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productPost('headline-page-api/add', productServerName, payload, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    /**
     * Deletes an existing headline page.
     * DELETE /bot/headline-page/delete/{page_id}
     */
    deleteHeadlinePage(pageId: number): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productDelete(`headline-page/delete/${pageId}`, productServerName, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    /**
     * Fetches all APIs for headline pages.
     * GET /bot/headline-page-apis
     */
    getHeadlinePageApis(pageId?: number): Observable<any> {
        const productServerName = environment.ai.productServerName;
        const params: any = {};
        if (pageId) params.headline_page_id = pageId;

        return this.apiService.productGet('headline-page-apis', productServerName, params, {
            showLoader: false,
            showErrorToaster: true
        });
    }

    /**
     * Deletes an existing API.
     * DELETE /bot/headline-page-api/delete/{api_id}
     */
    deleteHeadlinePageApi(apiId: number): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productDelete(`headline-page-api/delete/${apiId}`, productServerName, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    /**
     * Updates an existing API.
     * PUT /bot/headline-page-api/update/{api_id}
     */
    updateHeadlinePageApi(apiId: number, payload: any): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productPut(`headline-page-api/update/${apiId}`, productServerName, payload, {
            showLoader: false,
            showErrorToaster: true
        });
    }

    /**
     * Fetches all products.
     * GET /bot/products
     */
    getProducts(): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productGet('products', productServerName, {}, {
            showLoader: false,
            showErrorToaster: true
        });
    }

    /**
     * Authentication Configs (Data Sources)
     */

    getAuthConfigs(): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productGet('data-sources', productServerName, {}, {
            showLoader: false,
            showErrorToaster: true
        });
    }

    addAuthConfig(payload: any): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productPost('data-source/add', productServerName, payload, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    updateAuthConfig(id: number, payload: any): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productPut(`data-source/update/${id}`, productServerName, payload, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    deleteAuthConfig(id: number): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productDelete(`data-source/delete/${id}`, productServerName, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    getAgents(): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productGet('agents', productServerName, {}, {
            showLoader: false,
            showErrorToaster: true
        });
    }

    setupAgent(payload: any): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productPost('agent/setup', productServerName, payload, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    updateAgent(id: number, payload: { name: string; is_active?: boolean; selected_page_ids?: number[]; selected_api_ids?: number[] }): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productPut(`/agent/update/${id}`, productServerName, payload, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    deleteAgent(id: number): Observable<any> {
        const productServerName = environment.ai.productServerName;
        return this.apiService.productDelete(`/agent/delete/${id}`, productServerName, {
            showLoader: false,
            showErrorToaster: true,
            showSuccessToaster: false
        });
    }

    resolveAgent(productName: string, pageName: string, featureName: string, showErrorToaster = true): Observable<any> {
        const productServerName = environment.ai.productServerName;
        const params = {
            product_name: productName,
            page_name: pageName,
            feature_name: featureName
        };
        return this.apiService.productGet('agent/resolve', productServerName, params, {
            showLoader: false,
            showErrorToaster
        });
    }
}
